import { useCallback, useEffect, useMemo, useState } from 'react';

import { getAiPromptApplyConfigs } from '@/pages/datalab/services/ai/api';
import type { IAiPromptApplyConfigItem } from '@/pages/datalab/services/ai/api';
import {
  buildPromptInfoFromApplyConfig,
  normalizeApplyConfigItems,
} from '../utils/applyConfigUtils';
import type { IAiPromptInfo, IAiPromptOption } from '../types';

const applyConfigItemsCache = new Map<string, IAiPromptApplyConfigItem[]>();
const applyConfigRequestInFlightMap = new Map<string, Promise<IAiPromptApplyConfigItem[]>>();
const applyConfigRefreshListeners = new Set<() => void>();

const buildCacheKey = (gameCode: string, dashboardId: string): string =>
  `${gameCode}:${dashboardId}`;

/** 保存「下次自动应用」成功后清除缓存，使卡片与弹窗能读到最新配置 */
export const invalidateAiPromptApplyConfigCache = (
  gameCode?: string,
  dashboardId?: string,
): void => {
  if (gameCode && dashboardId) {
    applyConfigItemsCache.delete(buildCacheKey(gameCode, dashboardId));
    applyConfigRequestInFlightMap.delete(buildCacheKey(gameCode, dashboardId));
  } else {
    applyConfigItemsCache.clear();
    applyConfigRequestInFlightMap.clear();
  }
  applyConfigRefreshListeners.forEach((listener) => listener());
};

const subscribeApplyConfigRefresh = (listener: () => void): (() => void) => {
  applyConfigRefreshListeners.add(listener);
  return () => {
    applyConfigRefreshListeners.delete(listener);
  };
};

const fetchApplyConfigsWithDedupe = (
  gameCode: string,
  dashboardId: string,
): Promise<IAiPromptApplyConfigItem[]> => {
  if (!gameCode || !dashboardId) {
    return Promise.resolve([]);
  }
  const cacheKey = buildCacheKey(gameCode, dashboardId);
  const cached = applyConfigItemsCache.get(cacheKey);
  if (cached) {
    return Promise.resolve(cached);
  }
  const inFlight = applyConfigRequestInFlightMap.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }
  const requestPromise = getAiPromptApplyConfigs({
    game_code: gameCode,
    dashboard_id: dashboardId,
  })
    .then((response) => {
      const normalized = normalizeApplyConfigItems(response);
      applyConfigItemsCache.set(cacheKey, normalized);
      return normalized;
    })
    .finally(() => {
      applyConfigRequestInFlightMap.delete(cacheKey);
    });
  applyConfigRequestInFlightMap.set(cacheKey, requestPromise);
  return requestPromise;
};

export interface IUseAiPromptApplyConfigsParams {
  gameCode: string;
  dashboardId: string;
  allTaskIds: string[];
  /** 用于按 apply 配置的 name 还原预置分析思路选中态 */
  promptOptions: IAiPromptOption[];
  enabled: boolean;
}

export interface IUseAiPromptApplyConfigsResult {
  /** cardId -> 由 apply 配置转换的 promptInfo */
  applyPromptInfoByCardId: Record<string, IAiPromptInfo>;
  /** apply/get 是否仍在请求中（命中内存缓存时为 false） */
  isLoadingApplyConfigs: boolean;
}

/**
 * 拉取报表维度的「下次自动应用」配置，并按 card_id 映射为 IAiPromptInfo。
 * 仅预览态启用（`enabled: hasAiCard && !isEdit`）；编辑态以布局内 promptInfo 为准，不请求 apply/get。
 * 与布局 promptInfo 的合并优先级由 `resolveAiCardPromptInfo` 按预览/编辑态决定。
 */
export const useAiPromptApplyConfigs = (
  params: IUseAiPromptApplyConfigsParams,
): IUseAiPromptApplyConfigsResult => {
  const { gameCode, dashboardId, allTaskIds, promptOptions, enabled } = params;
  const [applyConfigItems, setApplyConfigItems] = useState<IAiPromptApplyConfigItem[]>([]);
  const [isLoadingApplyConfigs, setIsLoadingApplyConfigs] = useState<boolean>(false);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  const reloadApplyConfigs = useCallback(() => {
    setRefreshTick((prev) => prev + 1);
  }, []);

  useEffect(() => subscribeApplyConfigRefresh(reloadApplyConfigs), [reloadApplyConfigs]);

  useEffect(() => {
    let cancelled = false;
    if (!enabled || !gameCode || !dashboardId) {
      setApplyConfigItems([]);
      setIsLoadingApplyConfigs(false);
      return () => {
        cancelled = true;
      };
    }
    const cacheKey = buildCacheKey(gameCode, dashboardId);
    const cachedItems = applyConfigItemsCache.get(cacheKey);
    if (cachedItems) {
      setApplyConfigItems(cachedItems);
      setIsLoadingApplyConfigs(false);
      return () => {
        cancelled = true;
      };
    }
    setIsLoadingApplyConfigs(true);
    fetchApplyConfigsWithDedupe(gameCode, dashboardId)
      .then((items) => {
        if (cancelled) {
          return;
        }
        setApplyConfigItems(items);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setApplyConfigItems([]);
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        setIsLoadingApplyConfigs(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dashboardId, enabled, gameCode, refreshTick]);

  const applyPromptInfoByCardId = useMemo(() => {
    const result: Record<string, IAiPromptInfo> = {};
    for (const item of applyConfigItems) {
      const cardId = String(item.card_id || '');
      if (!cardId) {
        continue;
      }
      result[cardId] = buildPromptInfoFromApplyConfig(
        item,
        allTaskIds,
        promptOptions,
      );
    }
    return result;
  }, [allTaskIds, applyConfigItems, promptOptions]);

  return {
    applyPromptInfoByCardId,
    isLoadingApplyConfigs,
  };
};
